import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { withLDConsumer as featureFlagClient } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { useMerchantCreateCartAPI, useCartDetailsAPI } from './hooks/useAxios';
import { useProduct } from './hooks/useProduct';
import { useSkeleton } from './hooks/useSkeleton';
import useAuthentication from './modules/authentication/hooks/use-authentication';
import CloseWrapperBtn from './components/Button/CloseWrapperBtn';
import Popup from './components/Popup';
import LoadingPage from './components/LoadingPage';
import usePlatform, { defaultUrl } from './commons/hooks/use-platform';
import TermsPrivacyAbout from './components/TermsPrivacyAbout';
import { confirmPermissions } from './helpers/analyticsHelper';
import { usePaypal } from './hooks/paymentMethods/usePaypal';
import { useAmazon } from './hooks/paymentMethods/useAmazon';
import SkeletonLoading from './components/SkeletonLoading';
import usePortalSettings from './hooks/usePortalSettings';
import useReset from './hooks/useReset';
import { useStoreApi } from './hooks/useStoreApi';
import useCartTotals from './store/cartTotals/useCartTotals';
import useApiStatus from './store/apiStatus/useApiStatus';
import OrderDisplay from './components/OrderDisplay';
import OrderSubmit from './components/OrderSubmit';
import EditEmail from './components/EditEmail';
import DataContext from './store/dataContext';
import Checkout from './components/Checkout';
import Overlay from './components/Overlay';
import { useCheckoutSession } from './helpers/sessionHelper';
import { status } from './helpers/constants';
import UiContext from './store/uiContext';
import Cart from './components/Cart';
import Info from './components/Info';
import ExtraInfo from './components/ExtraInfo';
import { EVENT, trigger } from './commons/utils/custom-event';
import { setStatus, resetBilling } from './modules/billing/slice';
import { resetShipping, setPickupMethods } from './modules/shipping/slice';
import { postWithEmail } from './modules/authentication/slice';
import { fetchScript, handlePaymentScripts } from './hooks/useScripts';
import { forceNf, forcePlat, customerCodeUserId } from './helpers/prelaunchData';
import useCurrency from './modules/currency/hooks/use-currency';
import { environmentId, environment } from './helpers/urlConst';
import { applyDevelopmentUI } from './helpers/development';
import useTagManager from './hooks/useTagManager';

import './App.scss';

const App = ({ flags }) => {
  const {
    setIsCheckoutItemsCollapsed,
    setIsCheckoutTermsModalOpen,
    isCheckoutItemsCollapsed,
    allowManualAddress,
    showLoadingPage,
    showOverlay,
    editEmail,
    openPopup,
    done,
  } = useContext(UiContext);
  const { portalSettings, updatePortalSettings } = usePortalSettings();
  const { handlePaypalUrl } = usePaypal();
  const { handleAmazonUrl } = useAmazon();
  const { setShowLoading } = useSkeleton();
  const { allDigital } = useProduct();
  const { handleReset } = useReset();
  const { getLocalStorage } = useStoreApi();
  const { setAuthenticatedUser } = useAuthentication();
  const { isUserAuthenticated, logOut } = usePlatform();
  const { setData: setCartTotalsData } = useCartTotals();
  const { isSessionActive } = useCheckoutSession();
  const { set: setApiStatus } = useApiStatus();
  const {
    setRegisteredPaymentMethods,
    setIsProtectionApplied,
    setIsSignatureApplied,
    setUserWasAuthorized,
    setApiEmailResponse,
    updateHelpfulLinks,
    updateTotalsLoaded,
    updateCartDetails,
    setMoveToPlatform,
    updateWasLoggedIn,
    updateApiCoupons,
    orderSubmitInfo,
    orderSubmitting,
    updateGiftInfo,
    setIsLoggedIn,
    updateCartId,
    updateCoupon,
    wasLoggedIn,
    updateEmail,
    isLoggedIn,
    merchantId,
    storeUrl,
    device,
    cartId,
    email,
    updateAdditionalCharges,
  } = useContext(DataContext);
  const [apiPostEmailResponse, setApiPostEmailResponse] = useState({});
  const { postGAMessage } = useTagManager();

  const { parseCurrency } = useCurrency();

  const {
    apiResponse: apiMerchantCreateCartResponse,
    apiError: apiMerchantCreateCartError,
    apiMerchantCreateCart,
  } = useMerchantCreateCartAPI();
  const { apiResponse, apiError, apiCartDetails } = useCartDetailsAPI();
  const dispatch = useDispatch();
  const promiseWithEmail = useRef(null);

  const injectCustomerCode = () => {
    if (customerCodeUserId) {
      const script = document.createElement('script');
      if (environment === environmentId.prod) {
        script.src = `https://services.nofraud.com/js/{customerCodeUserId}/customer_code.js`;
      } else {
        script.src = `https://services.nofraud-test.com/js/${customerCodeUserId}/customer_code.js`;
      }
      document.body.appendChild(script);
    }
  };

  useEffect(() => {
    postGAMessage('page_view', {});
    setUserWasAuthorized(false);
    apiMerchantCreateCart(merchantId);
    handlePaypalUrl();
    handleAmazonUrl();
    injectCustomerCode();
    window.getVersion = () => {
      try {
        // eslint-disable-next-line global-require
        return require('../package.json').version;
      } catch (err) {
        return err;
      }
    };
  }, []);

  useEffect(() => {
    if (flags['disable-bigcom-checkout-form'] === true) {
      window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
    }
  }, [flags]);

  const doLogOut = async () => {
    await handleReset();
    updateWasLoggedIn(false);
    setIsLoggedIn(false);
    setUserWasAuthorized(true);
    await logOut();
  };

  const lowerize = obj =>
    Object.keys(obj).reduce((acc, k) => {
      const val = acc;
      val[k.toLowerCase()] = obj[k];
      return val;
    }, {});

  const checkIfMethodIsOnPlatform = (objName, obj, platList = {}) => {
    const lowerCasePlatList = lowerize(platList);
    if (lowerCasePlatList[objName?.toLowerCase()]) {
      return undefined;
    }
    return obj;
  };

  const removeEmpty = obj => {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] === Object(obj[key])) newObj[key] = removeEmpty(obj[key]);
      else if (obj[key] !== undefined) newObj[key] = obj[key];
    });
    return newObj;
  };

  useEffect(() => {
    if (apiMerchantCreateCartResponse && device?.isReady) {
      if (
        apiMerchantCreateCartResponse.settings?.abTesting?.isEnabled &&
        apiMerchantCreateCartResponse.settings?.abTesting?.partition === 0 &&
        !forceNf
      ) {
        localStorage.removeItem('ab-testing-hash');
        window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
      }
      if (forcePlat) {
        window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
      }
      updateCartId(apiMerchantCreateCartResponse.cartId);
      const customLinks = {};
      for (let x = 0; x < apiMerchantCreateCartResponse?.links?.customLinks?.length; x++) {
        const item = apiMerchantCreateCartResponse?.links?.customLinks[x];
        customLinks[item.label] = item.link;
      }
      updateHelpfulLinks({
        ...apiMerchantCreateCartResponse.links,
        ...customLinks,
      });
      updatePortalSettings(apiMerchantCreateCartResponse.settings, apiMerchantCreateCartResponse.plugin);
      if (!apiMerchantCreateCartResponse.settings?.contactPhone?.isRequired) {
        dispatch(setStatus({ isReady: true }));
      }

      if (apiMerchantCreateCartResponse?.paymentMethods) {
        const apiPaymentMethods = removeEmpty({
          ...apiMerchantCreateCartResponse.platformPaymentMethods,
          amazonpay: checkIfMethodIsOnPlatform(
            'amazonpay',
            apiMerchantCreateCartResponse.paymentMethods?.amazonpay ||
              apiMerchantCreateCartResponse.paymentMethods?.amazonPay,
            apiMerchantCreateCartResponse.platformPaymentMethods,
          ),
          paypal: {
            ...apiMerchantCreateCartResponse.paymentMethods?.paypal,
            isEnabled: apiMerchantCreateCartResponse.paymentMethods?.paypal?.isEnabled,
          },
          paylater: checkIfMethodIsOnPlatform(
            'paylater',
            apiMerchantCreateCartResponse.paymentMethods?.paypal?.paylater,
            apiMerchantCreateCartResponse.platformPaymentMethods,
          ),
          venmo: checkIfMethodIsOnPlatform(
            'venmo',
            apiMerchantCreateCartResponse.paymentMethods?.paypal?.venmo,
            apiMerchantCreateCartResponse.platformPaymentMethods,
          ),
          googlepay: {
            ...apiMerchantCreateCartResponse.paymentMethods?.gpay,
            isEnabled: !!apiMerchantCreateCartResponse.paymentMethods?.gpay?.gpayKeys,
          },
        });
        delete apiPaymentMethods.gpay;
        setRegisteredPaymentMethods(apiPaymentMethods);
        setMoveToPlatform(apiMerchantCreateCartResponse.platformPaymentMethods || {});
        if (apiPaymentMethods?.paypal) {
          handlePaymentScripts(apiPaymentMethods);
        }
      }
      apiCartDetails(apiMerchantCreateCartResponse.cartId, device?.id);
      const activeProtection = apiMerchantCreateCartResponse?.plugin?.shippingProtection?.sku;
      const activeSignature = apiMerchantCreateCartResponse?.plugin?.customerSignature?.sku;
      const charges = [];
      if (activeProtection) {
        charges.push({
          name: 'Package Protection',
          qty: 1,
          value: apiMerchantCreateCartResponse?.plugin?.shippingProtection.quote,
        });
        setIsProtectionApplied(true);
      } else {
        setIsProtectionApplied(false);
      }
      if (activeSignature) {
        charges.push({
          name: 'Signature Required',
          qty: 1,
          value: apiMerchantCreateCartResponse?.plugin?.customerSignature.quote,
        });
        setIsSignatureApplied(true);
      } else {
        setIsSignatureApplied(false);
      }
      updateAdditionalCharges(charges);
      if (apiMerchantCreateCartResponse.pickupMethods) {
        dispatch(setPickupMethods(apiMerchantCreateCartResponse.pickupMethods));
      }
    } else if (apiMerchantCreateCartError && device?.isReady) {
      window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
    }
  }, [apiMerchantCreateCartResponse, apiMerchantCreateCartError, device?.isReady]);

  useEffect(() => {
    if (apiResponse) {
      if (apiResponse.suggest_email && email && email !== apiResponse.suggest_email && apiResponse?.isUserLoggedIn) {
        dispatch(resetShipping());
        dispatch(resetBilling());
        if (!apiMerchantCreateCartResponse.settings?.contactPhone?.isRequired) {
          dispatch(setStatus({ isReady: true }));
        }
      }
      const activeCoupon = apiResponse.cart.coupons[0] || {};
      const activeGiftCertificates = apiResponse?.cart?.giftCertificates?.length > 0;
      updateApiCoupons(apiResponse.cart.coupons);
      if (!isUserAuthenticated(apiResponse) && !isLoggedIn && !isSessionActive) {
        setShowLoading(false);
      }
      if (apiResponse.suggest_email) {
        updateEmail(apiResponse.suggest_email);
        setAuthenticatedUser(isUserAuthenticated(apiResponse));
      } else if (email) {
        setAuthenticatedUser(isUserAuthenticated(apiResponse));
      }
      if (wasLoggedIn && !apiResponse?.isUserLoggedIn) {
        doLogOut();
      }
      updateCartDetails(apiResponse.cart);
      if (Object.keys(apiResponse.cartTotals || {})?.length > 0) {
        setCartTotalsData(apiResponse.cartTotals);
      }
      updateCoupon(activeCoupon);
      if (activeGiftCertificates) {
        updateGiftInfo(apiResponse?.cart?.giftCertificates);
      }
      updateTotalsLoaded(true);
      if (allDigital()) {
        setShowLoading(false);
      }
      parseCurrency(apiResponse);
      postGAMessage('begin_checkout', {
        currency: apiResponse.cart?.currency?.baseCurrency,
        value: apiResponse.cart?.totals?.total,
        items: apiResponse.cart?.lineItems.map((item, index) => ({
          currency: apiResponse.cart?.currency?.baseCurrency,
          index,
          item_name: item.name,
          item_id: String(item.platformLineId),
          item_category: item.productType,
          item_category_2: item.category,
          price: item.price,
          quantity: item.qty,
        })),
      });
    }
  }, [apiResponse]);

  useEffect(() => {
    const checkLogin = async () => {
      if (isLoggedIn) {
        setShowLoading(true);
        const payload = {
          cartId,
          data: {
            email,
            deviceId: device?.id,
          },
        };
        promiseWithEmail.current = dispatch(postWithEmail(payload));
        const result = await promiseWithEmail.current.unwrap();
        setApiPostEmailResponse(result);
        setApiStatus({ isEmailReady: status.READY });
      } else if (promiseWithEmail.current) {
        promiseWithEmail.current.abort();
        setApiPostEmailResponse({});
        setApiStatus({ isEmailReady: status.IDLE });
      }
    };
    checkLogin();
  }, [isLoggedIn]);

  useEffect(() => {
    setTimeout(() => {
      document.body.classList.remove('preload');
    }, 7500);
  });

  useEffect(() => {
    confirmPermissions();
    if (navigator.appVersion.includes('iPhone')) {
      document.body.classList.add('iphone-styles');
    }
    if (navigator.appVersion.includes('Android')) {
      document.body.classList.add('android-styles');
      window.addEventListener('click', () => {
        if (document.activeElement.tagName === 'INPUT') {
          document.activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }
      });
    }
    applyDevelopmentUI();
  }, []);

  useEffect(() => {
    if (apiPostEmailResponse?.success) {
      setApiEmailResponse(apiPostEmailResponse);
      trigger(EVENT.EMAIL_RESPONSE_READY, apiPostEmailResponse);
    }
  }, [apiPostEmailResponse]);

  useEffect(() => {
    if (portalSettings?.scripts && portalSettings?.scripts?.length > 0) {
      for (let x = 0; x < portalSettings?.scripts?.length; x++) {
        const setting = portalSettings?.scripts[x];
        if (setting.scriptLocation === 'All pages' || setting.scriptLocation === 'Checkout') {
          if (setting?.parent) {
            window.parent.postMessage(
              JSON.stringify({
                task: 'appendScript',
                url: setting?.url,
                content: setting?.content,
                type: setting?.type?.toLowerCase(),
              }),
              '*',
            );
          } else {
            const script = document.getElementById(`script-${x}`);
            if (script) {
              document.body.removeChild(script);
            }
            fetchScript(setting, x);
          }
        }
      }
    }
    if (portalSettings?.socialImpact?.searchStore) {
      getLocalStorage(portalSettings?.socialImpact?.searchStore);
    }
  }, [window.location.href, portalSettings]);

  const mobileItemsCollapsed = ({ id }) => {
    if (id === 'app-sidebar') {
      setIsCheckoutItemsCollapsed(!isCheckoutItemsCollapsed);
    }
  };

  const body = () => (
    <>
      <div
        style={{
          display: apiResponse === undefined ? 'block' : 'none',
        }}
      >
        <SkeletonLoading />
      </div>
      <div
        style={{
          display: apiResponse === undefined ? 'none' : 'block',
          maxHeight: '100vh',
          width: '100%',
        }}
      >
        <div className="app-container">
          <div className="app-wrapper">
            <div className={`app-main ${isCheckoutItemsCollapsed ? 'mb' : ''}`}>
              {!done || orderSubmitInfo?.redirectUrl ? (
                <Checkout setOrderSubmitting={orderSubmitting} />
              ) : (
                <OrderDisplay orderInfo={orderSubmitInfo} />
              )}
            </div>
            <div
              className={classNames({
                active: isCheckoutItemsCollapsed,
                'app-sidebar': true,
              })}
              id="app-sidebar"
              role="button"
              tabIndex="0"
              onKeyDown={null}
              onClick={event => mobileItemsCollapsed(event.target)}
            >
              <Cart type="default" />
              <Info isEnterAddressManuallyOpened={allowManualAddress} openTerms={setIsCheckoutTermsModalOpen} />
              <CloseWrapperBtn />
            </div>
            <OrderSubmit />
            {showLoadingPage && <LoadingPage />}
            {editEmail && <EditEmail />}
            {showOverlay && <Overlay />}
            {openPopup && <Popup />}
            <TermsPrivacyAbout />
          </div>
          {done && <ExtraInfo />}
        </div>
      </div>
    </>
  );

  if (apiError) {
    window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
  }

  if (window.location.href.indexOf('amazon=openPopup') > -1) {
    return null;
  }

  return body();
};

App.propTypes = {
  flags: PropTypes.shape({
    'disable-bigcom-checkout-form': PropTypes.bool,
  }),
};

export default featureFlagClient()(App);
